<template>
  <Layout>
    <PageHeader :title="pageTitle" :items="items"></PageHeader>
    <div class="row">
      <div class="col-12">
        <div class="card">
          <div class="card-body">
            <div class="d-flex justify-content-between align-items-center mb-3">
              <router-link :to="{ name: 'community-chat' }" class="btn btn-sm btn-light">
                <i class="mdi mdi-arrow-left"></i> Back to Groups
              </router-link>
              <button class="btn btn-sm btn-light" @click="refresh">
                <i class="mdi mdi-refresh"></i> Refresh
              </button>
            </div>

            <!-- Message thread -->
            <div ref="thread" class="chat-thread border rounded p-3 mb-3">
              <p v-if="!messages.length" class="text-center text-muted my-4">
                No messages yet.
              </p>
              <div
                v-for="m in messages"
                :key="m._id"
                class="d-flex mb-2"
                :class="isAdminMsg(m) ? 'justify-content-end' : 'justify-content-start'"
              >
                <div
                  class="msg-bubble px-3 py-2 rounded"
                  :class="isAdminMsg(m) ? 'bubble-admin' : 'bubble-member'"
                >
                  <div class="d-flex justify-content-between align-items-start">
                    <div class="small font-weight-bold mb-1">
                      {{ senderLabel(m) }}
                    </div>
                    <span
                      v-if="can('send-community-group-message')"
                      class="reply-btn ml-2"
                      title="Reply"
                      @click="startReply(m)"
                    >↩</span>
                  </div>
                  <!-- quoted reply -->
                  <div v-if="m.replyPreview" class="reply-quote px-2 py-1 mb-1">
                    <div class="small font-weight-bold">
                      {{ replyAuthor(m.replyPreview) }}
                    </div>
                    <div class="small text-muted text-truncate">
                      {{ m.replyPreview.snippet }}
                    </div>
                  </div>
                  <div v-if="m.type === 'image'">
                    <img
                      :src="m.media && (m.media.thumbnailUrl || m.media.url)"
                      class="msg-media"
                      @click="openMedia(m)"
                    />
                  </div>
                  <div v-else-if="m.type === 'video'">
                    <video :src="m.media && m.media.url" class="msg-media" controls></video>
                  </div>
                  <div v-else-if="m.type === 'link'">
                    <a :href="m.body" target="_blank">
                      🔗 {{ (m.linkPreview && m.linkPreview.title) || m.body }}
                    </a>
                  </div>
                  <div v-else class="msg-body">
                    {{ m.body }}
                  </div>
                  <div class="small text-muted mt-1 text-right">
                    {{ formatTime(m.createdAt) }}
                  </div>
                </div>
              </div>
            </div>

            <!-- Composer -->
            <div v-if="can('send-community-group-message')" class="compose">
              <div
                v-if="replyingTo"
                class="reply-bar d-flex justify-content-between align-items-center px-2 py-1 mb-2"
              >
                <div class="text-truncate">
                  <div class="small font-weight-bold">
                    Replying to {{ senderLabel(replyingTo) }}
                  </div>
                  <div class="small text-muted text-truncate">
                    {{ replySnippet(replyingTo) }}
                  </div>
                </div>
                <span class="cancel-reply ml-2" @click="cancelReply">✕</span>
              </div>
              <div class="d-flex align-items-end">
                <label class="attach-btn mr-2 mb-0" title="Attach image/video">
                  📎
                  <input type="file" accept="image/*,video/*" hidden @change="onPickFile" />
                </label>
                <b-form-textarea
                  v-model="form.body"
                  class="flex-grow-1"
                  :placeholder="`Type a message to send as ${officialSenderName}…`"
                  rows="2"
                  max-rows="6"
                  @keydown.enter.exact.prevent="sendMessage"
                ></b-form-textarea>
              </div>
              <div class="d-flex justify-content-end mt-2">
                <button
                  class="btn btn-primary"
                  :disabled="!form.body || !form.body.trim()"
                  @click="sendMessage"
                >
                  Send as {{ officialSenderName }}
                </button>
              </div>
            </div>
            <p v-else class="text-muted mb-0">
              You don't have permission to send messages in this group.
            </p>
          </div>
        </div>
      </div>
    </div>

    <!-- Full-screen media viewer (WhatsApp style) -->
    <div v-if="viewer" class="media-viewer" @click.self="closeMedia">
      <div class="media-viewer-bar">
        <span class="viewer-close mr-3" @click="closeMedia">✕</span>
        <div class="text-truncate">
          <div class="font-weight-bold">{{ viewer.label }}</div>
          <div class="small viewer-time">{{ viewer.time }}</div>
        </div>
        <a
          :href="viewer.url"
          target="_blank"
          rel="noopener noreferrer"
          class="viewer-link ml-auto"
        >Open original</a>
      </div>
      <div class="media-viewer-body" @click.self="closeMedia">
        <img v-if="viewer.type !== 'video'" :src="viewer.url" class="media-viewer-media" />
        <video v-else :src="viewer.url" class="media-viewer-media" controls autoplay></video>
      </div>
    </div>
  </Layout>
</template>

<script>
import Layout from "@/views/layouts/main";
import PageHeader from "@/components/page-header";
import MixinRequest from "@/mixins/request";
import communityChatMixin from "@/mixins/ModuleJs/community-chat";

export default {
  data() {
    return {
      items: [
        { text: "Home", href: "/" },
        { text: "Community Chat", href: "/community-chat" },
        { text: "Chat" },
      ],
      viewer: null,
    };
  },
  mixins: [communityChatMixin, MixinRequest],
  components: {
    Layout,
    PageHeader,
  },
  computed: {
    pageTitle() {
      return this.groupName ? this.groupName : "Group Chat";
    },
  },
  async created() {
    this.groupId = this.$route.params.id;
    this.groupName = this.$route.query.name || "";
    this.loadingMessages = true;
    await this.fetchMessages(this.groupId);
    this.loadingMessages = false;
    this.startPolling(this.groupId);
  },
  mounted() {
    window.addEventListener("keydown", this.onViewerKey);
  },
  beforeDestroy() {
    this.stopPolling();
    window.removeEventListener("keydown", this.onViewerKey);
  },
  methods: {
    async refresh() {
      await this.fetchMessages(this.groupId);
    },
    openMedia(m) {
      const url = m.media && m.media.url;
      if (!url) return;
      this.viewer = {
        url,
        type: m.type,
        label: this.senderLabel(m),
        time: this.formatTime(m.createdAt),
      };
    },
    closeMedia() {
      this.viewer = null;
    },
    onViewerKey(e) {
      if (e.key === "Escape") this.closeMedia();
    },
    replyAuthor(rp) {
      if (!rp) return "";
      if (rp.senderName) return rp.senderName;
      if (rp.senderId === "news") return this.newsSenderName;
      return rp.senderId === "system"
        ? this.officialSenderName
        : "Member " + rp.senderId;
    },
  },
};
</script>

<style scoped>
.chat-thread {
  height: 55vh;
  overflow-y: auto;
  background: #f7f7f9;
}
.msg-bubble {
  max-width: min(70%, 520px);
  width: fit-content;
  word-break: break-word;
  overflow-wrap: anywhere;
}
.bubble-member {
  background: #ffffff;
  border: 1px solid #e5e5e5;
}
.bubble-admin {
  background: #d7e3ff;
  border: 1px solid #b9ccf7;
}
.reply-btn {
  cursor: pointer;
  opacity: 0.5;
  font-size: 15px;
  line-height: 1;
}
.reply-btn:hover {
  opacity: 1;
}
.reply-quote {
  border-left: 3px solid #3d50c4;
  background: rgba(0, 0, 0, 0.05);
  border-radius: 4px;
}
.msg-media {
  max-width: 240px;
  max-height: 240px;
  object-fit: contain;
  border-radius: 6px;
  cursor: pointer;
  display: block;
}
.media-viewer {
  position: fixed;
  inset: 0;
  z-index: 1080;
  background: #000;
  display: flex;
  flex-direction: column;
}
.media-viewer-bar {
  display: flex;
  align-items: center;
  flex: none;
  padding: 10px 16px;
  color: #fff;
  background: rgba(0, 0, 0, 0.6);
}
.media-viewer-body {
  flex: 1;
  min-height: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 12px;
}
.media-viewer-media {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}
.viewer-close {
  cursor: pointer;
  font-size: 20px;
  line-height: 1;
}
.viewer-time {
  opacity: 0.75;
}
.viewer-link {
  color: rgba(255, 255, 255, 0.85);
  text-decoration: underline;
  font-size: 13px;
  flex: none;
}
.reply-bar {
  border-left: 4px solid #3d50c4;
  background: #eef1ff;
  border-radius: 4px;
}
.cancel-reply {
  cursor: pointer;
  color: #667781;
  font-size: 18px;
}
.attach-btn {
  font-size: 22px;
  cursor: pointer;
  line-height: 38px;
}
</style>
